import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getSubscription } from "../helpers/subscription_helper";
import { getSendingSetup } from "../helpers/sending_setup_helper";
import { getByoCircuitSettings } from "../helpers/byo_circuit_helper";
import { getInstalledIntegrations } from "../helpers/integrations_helper";
import { getAbandonedCartAutomation } from "../helpers/automations_helper";
import { getPendingApprovals } from "../helpers/sends_helper";
import { fetchApiMessageHistory } from "../helpers/api_messages_helper";
import { canApproveSend, isOwner } from "../utils/tenantRoles";
import { openTwoColumnSubmenu } from "./twoColumnPanel";
import {
  readShellFavorites,
  type ShellFavorite,
} from "../helpers/shellFavorites";

/**
 * Menu tenant — jornada Carla: gente → envio → automação → integrações → conta (DOC 09 § 7.3).
 * Labels = chaves i18n; TwoColumnLayout aplica t().
 * Two Column: 1 link → navega no ícone; 2+ → abre 2ª coluna.
 * Carrinho abandonado só aparece com app ecommerce.sync ativo (DOC 03 § 4.14 · 11).
 */
const Navdata = () => {
    const history = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const owner = isOwner();
    const canApprove = canApproveSend();
    const [isDashboard, setIsDashboard] = useState(false);
    const [isFavorites, setIsFavorites] = useState(false);
    const [isAudience, setIsAudience] = useState(false);
    const [isCampaigns, setIsCampaigns] = useState(false);
    const [isAutomation, setIsAutomation] = useState(false);
    const [isIntegrations, setIsIntegrations] = useState(false);
    const [isAccount, setIsAccount] = useState(false);
    const [iscurrentState, setIscurrentState] = useState("Dashboard");
    const [shellFavorites, setShellFavorites] = useState<ShellFavorite[]>(() =>
        readShellFavorites()
    );
    // Badge “Promo” em Plano e cota enquanto houver cupom ativo (LAC-089 polish).
    const [hasActiveCoupon, setHasActiveCoupon] = useState(false);
    // Badge “Pendente” em Setup de envio até DNS/SES/SSL ok — onboarding sem procurar.
    const [setupIncomplete, setSetupIncomplete] = useState(false);
    // Controles de circuit só para tenant com AWS própria (BYO).
    const [hasByoCircuit, setHasByoCircuit] = useState(false);
    // Menu Automação → Carrinho: só com loja ecommerce conectada (VTEX hoje; Shopify depois).
    const [hasAbandonedCartApp, setHasAbandonedCartApp] = useState(false);
    const [abandonedCartToday, setAbandonedCartToday] = useState(0);

    // LAC-155: contador do menu para envios aguardando a minha aprovação.
    const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
    // Falhas de POST /messages hoje — badge em Tokens de API.
    const [apiFailedToday, setApiFailedToday] = useState(0);

    function updateIconSidebar(e: any) {
        if (e?.target?.getAttribute("sub-items")) {
            const ul: any = document.getElementById("two-column-menu");
            if (!ul) return;
            const iconItems: any = ul.querySelectorAll(".nav-icon.active");
            [...iconItems].forEach((item: any) => {
                item.classList.remove("active");
                const id = item.getAttribute("sub-items");
                const getID = document.getElementById(id) as HTMLElement | null;
                getID?.classList.remove("show");
            });
        }
    }

    // Não mexer em twocolumn-panel aqui — TwoColumnLayout sincroniza por rota
    // (folha 1 link = só ícones). Antes este effect forçava a 2ª coluna aberta.
    useEffect(() => {
        if (iscurrentState !== "Dashboard") setIsDashboard(false);
        if (iscurrentState !== "Favorites") setIsFavorites(false);
        if (iscurrentState !== "Audience") setIsAudience(false);
        if (iscurrentState !== "Campaigns") setIsCampaigns(false);
        if (iscurrentState !== "Automation") setIsAutomation(false);
        if (iscurrentState !== "Integrations") setIsIntegrations(false);
        if (iscurrentState !== "Account") setIsAccount(false);
    }, [
        history,
        iscurrentState,
        isDashboard,
        isFavorites,
        isAudience,
        isCampaigns,
        isAutomation,
        isIntegrations,
        isAccount,
    ]);

    useEffect(() => {
        const onFav = () => setShellFavorites(readShellFavorites());
        window.addEventListener("brenvio:shell-favorites-updated", onFav);
        return () =>
            window.removeEventListener("brenvio:shell-favorites-updated", onFav);
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (owner) {
            getSubscription()
                .then((res) => {
                    if (!cancelled) setHasActiveCoupon(!!res?.data?.active_coupon);
                })
                .catch(() => {
                    if (!cancelled) setHasActiveCoupon(false);
                });
            getSendingSetup()
                .then((res) => {
                    if (!cancelled) setSetupIncomplete(!res?.data?.is_ready);
                })
                .catch(() => {
                    // Falha de rede ≠ setup incompleto — não alarmar sem evidência.
                    if (!cancelled) setSetupIncomplete(false);
                });
            getByoCircuitSettings()
                .then((res) => {
                    if (!cancelled) setHasByoCircuit(!!res?.data?.available);
                })
                .catch(() => {
                    if (!cancelled) setHasByoCircuit(false);
                });
        }
        getInstalledIntegrations()
            .then((res) => {
                if (cancelled) return;
                const rows = res?.data || [];
                // Qualquer app ecommerce.sync ativo desbloqueia Automação → Carrinho.
                const hasStore = rows.some(
                    (r) =>
                        r.enabled &&
                        r.capability === "ecommerce.sync"
                );
                setHasAbandonedCartApp(hasStore);
                if (!hasStore) {
                    setAbandonedCartToday(0);
                    return;
                }
                getAbandonedCartAutomation()
                    .then((auto) => {
                        if (!cancelled) {
                            setAbandonedCartToday(
                                auto?.data?.metrics?.events_today ?? 0
                            );
                        }
                    })
                    .catch(() => {
                        if (!cancelled) setAbandonedCartToday(0);
                    });
            })
            .catch(() => {
                if (!cancelled) {
                    setHasAbandonedCartApp(false);
                    setAbandonedCartToday(0);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [location.pathname, owner]);

    useEffect(() => {
        if (!canApprove) {
            setPendingApprovalCount(0);
            return;
        }

        let cancelled = false;
        void (async () => {
            try {
                const res = await getPendingApprovals({
                    page: 1,
                    per_page: 1,
                });
                if (cancelled) return;
                setPendingApprovalCount(res.meta?.total ?? 0);
            } catch {
                if (cancelled) return;
                setPendingApprovalCount(0);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [location.pathname, canApprove]);

    // Badge Tokens: failed_today com cache 60s (menos GET a cada navegação).
    useEffect(() => {
        let cancelled = false;
        const CACHE_KEY = "brenvio:menu:api_failed_today";
        const TTL_MS = 60_000;

        try {
            const raw = sessionStorage.getItem(CACHE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw) as {
                    count?: number;
                    at?: number;
                };
                if (
                    typeof parsed.count === "number" &&
                    typeof parsed.at === "number" &&
                    Date.now() - parsed.at < TTL_MS
                ) {
                    setApiFailedToday(parsed.count);
                    return;
                }
            }
        } catch {
            // ignore cache
        }

        void (async () => {
            try {
                const res = await fetchApiMessageHistory({
                    source: "api",
                    page: 1,
                    per_page: 1,
                });
                if (cancelled) return;
                const count = res.data?.meta?.failed_today ?? 0;
                setApiFailedToday(count);
                try {
                    sessionStorage.setItem(
                        CACHE_KEY,
                        JSON.stringify({ count, at: Date.now() })
                    );
                } catch {
                    // ignore
                }
            } catch {
                if (cancelled) return;
                setApiFailedToday(0);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [location.pathname]);

    const menuItems: any = [
        { label: "menu.navHeader", isHeader: true },
        {
            id: "dashboard",
            label: "dashboard.home",
            icon: "ri-home-4-line",
            link: "/#",
            stateVariables: isDashboard,
            click: function (e: any) {
                e.preventDefault();
                setIsDashboard(!isDashboard);
                setIscurrentState("Dashboard");
                updateIconSidebar(e);
            },
            subItems: [
                {
                    id: "dashboard-home",
                    label: "dashboard.title",
                    link: "/dashboard",
                    parentId: "dashboard",
                },
            ],
        },
        ...(shellFavorites.length > 0
            ? [
                  {
                      id: "favorites",
                      label: "menu.favorites",
                      icon: "ri-star-line",
                      link: "/#",
                      stateVariables: isFavorites,
                      click: function (e: any) {
                          e.preventDefault();
                          openTwoColumnSubmenu();
                          setIsFavorites(!isFavorites);
                          setIscurrentState("Favorites");
                          updateIconSidebar(e);
                      },
                      subItems: shellFavorites.map((fav) => ({
                          id: `fav-${fav.id}`,
                          label: fav.label,
                          link: fav.link,
                          parentId: "favorites",
                      })),
                  },
              ]
            : []),
        {
            id: "audience",
            label: "menu.audience",
            icon: "ri-contacts-book-2-line",
            link: "/#",
            stateVariables: isAudience,
            click: function (e: any) {
                e.preventDefault();
                openTwoColumnSubmenu();
                setIsAudience(!isAudience);
                setIscurrentState("Audience");
                updateIconSidebar(e);
            },
            // Ordem = frequência Carla: lista → import → classificação → schema → higiene.
            subItems: [
                {
                    id: "contacts",
                    label: "menu.contacts",
                    link: "/contacts",
                    parentId: "audience",
                },
                {
                    id: "contacts-import",
                    label: "menu.importContacts",
                    link: "/contacts/import",
                    parentId: "audience",
                },
                {
                    id: "tags",
                    label: "menu.tags",
                    link: "/tags",
                    parentId: "audience",
                },
                {
                    id: "segments",
                    label: "menu.segments",
                    link: "/segments",
                    parentId: "audience",
                },
                {
                    id: "contacts-inactive",
                    label: "menu.inactiveContacts",
                    link: "/contacts/inactive",
                    parentId: "audience",
                },
                {
                    id: "settings-contact-fields",
                    label: "menu.contactFields",
                    link: "/settings/contact-fields",
                    parentId: "audience",
                },
                {
                    id: "contacts-blocklist",
                    label: "menu.blocklist",
                    link: "/contacts/blocklist",
                    parentId: "audience",
                },
            ],
        },
        {
            id: "campaigns",
            label: "menu.campaignsNav",
            icon: "ri-mail-send-line",
            link: "/#",
            stateVariables: isCampaigns,
            click: function (e: any) {
                e.preventDefault();
                openTwoColumnSubmenu();
                setIsCampaigns(!isCampaigns);
                setIscurrentState("Campaigns");
                updateIconSidebar(e);
            },
            subItems: [
                {
                    id: "campaigns-list",
                    label: "menu.campaigns",
                    link: "/campaigns",
                    parentId: "campaigns",
                },
                {
                    id: "sends",
                    label: "menu.sends",
                    link: "/sends",
                    parentId: "campaigns",
                },
                ...(canApprove
                    ? [
                          {
                              id: "sends-pending-approval",
                              label: "menu.sendsPendingMine",
                              link: "/sends?pending=mine",
                              parentId: "campaigns",
                              ...(pendingApprovalCount > 0
                                  ? {
                                        badgeName: String(
                                            pendingApprovalCount
                                        ),
                                        badgeColor: "warning",
                                    }
                                  : {}),
                          },
                      ]
                    : []),
                // Subnível Modelos + galeria (LAC-124.2) — Two Column já renderiza childItems.
                {
                    id: "templates-group",
                    label: "menu.templatesGroup",
                    link: "/#",
                    parentId: "campaigns",
                    isChildItem: true,
                    stateVariables: true,
                    childItems: [
                        {
                            id: "templates",
                            label: "menu.templates",
                            link: "/templates",
                            parentId: "templates-group",
                        },
                        {
                            id: "platform-templates-gallery",
                            label: "menu.platformTemplates",
                            link: "/templates/gallery",
                            parentId: "templates-group",
                        },
                    ],
                },
            ],
        },
        {
            id: "automation",
            label: "menu.automation",
            icon: "ri-flow-chart",
            link: "/#",
            stateVariables: isAutomation,
            click: function (e: any) {
                e.preventDefault();
                openTwoColumnSubmenu();
                setIsAutomation(!isAutomation);
                setIscurrentState("Automation");
                updateIconSidebar(e);
            },
            subItems: [
                {
                    id: "forms",
                    label: "menu.forms",
                    link: "/forms",
                    parentId: "automation",
                },
                {
                    id: "rss-feeds",
                    label: "menu.rssFeeds",
                    link: "/rss-feeds",
                    parentId: "automation",
                },
                {
                    id: "automations-welcome",
                    label: "menu.welcomeAutomation",
                    link: "/automations/welcome",
                    parentId: "automation",
                },
                {
                    id: "automations-win-back",
                    label: "menu.winBackAutomation",
                    link: "/automations/win-back",
                    parentId: "automation",
                },
                ...(hasAbandonedCartApp
                    ? [
                          {
                              id: "automations-abandoned-cart",
                              label: "menu.abandonedCartAutomation",
                              link: "/automations/abandoned-cart?today=1",
                              parentId: "automation",
                              ...(abandonedCartToday > 0
                                  ? {
                                        badgeName: String(abandonedCartToday),
                                        badgeColor: "info",
                                    }
                                  : {}),
                          },
                      ]
                    : []),
            ],
        },
        {
            id: "integrations",
            label: "menu.integrations",
            icon: "ri-plug-line",
            link: "/#",
            stateVariables: isIntegrations,
            click: function (e: any) {
                e.preventDefault();
                openTwoColumnSubmenu();
                setIsIntegrations(!isIntegrations);
                setIscurrentState("Integrations");
                updateIconSidebar(e);
            },
            subItems: [
                {
                    id: "integrations-hub",
                    label: "menu.integrationsHub",
                    link: "/integrations",
                    parentId: "integrations",
                },
                {
                    id: "settings-api-tokens",
                    label: "menu.apiTokens",
                    link:
                        apiFailedToday > 0
                            ? "/settings/api-tokens?api_messages=failed"
                            : "/settings/api-tokens",
                    parentId: "integrations",
                    ...(apiFailedToday > 0
                        ? {
                              badgeName: String(apiFailedToday),
                              badgeColor: "danger",
                              badgeTitle: t("menu.apiTokensFailedBadge", {
                                  count: apiFailedToday,
                              }),
                          }
                        : {}),
                },
                {
                    id: "settings-webhooks",
                    label: "menu.webhooks",
                    link: "/settings/webhooks",
                    parentId: "integrations",
                },
            ],
        },
        {
            id: "account",
            label: "menu.account",
            icon: "ri-settings-3-line",
            link: "/#",
            stateVariables: isAccount,
            click: function (e: any) {
                e.preventDefault();
                openTwoColumnSubmenu();
                setIsAccount(!isAccount);
                setIscurrentState("Account");
                updateIconSidebar(e);
            },
            // Setup alto = onboarding; plano = Roberto; perfil por último.
            subItems: [
                ...(owner ? [{
                    id: "settings-sending",
                    label: "menu.sendingSetup",
                    link: "/settings/sending",
                    parentId: "account",
                    ...(setupIncomplete
                        ? {
                              badgeName: t("menu.setupIncompleteBadge"),
                              badgeColor: "warning",
                          }
                        : {}),
                },
                ...(hasByoCircuit
                    ? [{
                          id: "settings-sending-controls",
                          label: "menu.sendingControls",
                          link: "/settings/sending-controls",
                          parentId: "account",
                      }]
                    : []),
                {
                    id: "settings-deliverability",
                    label: "menu.deliverability",
                    link: "/settings/deliverability",
                    parentId: "account",
                },
                {
                    id: "settings-subscription",
                    label: "menu.subscription",
                    link: "/settings/subscription",
                    parentId: "account",
                    ...(hasActiveCoupon
                        ? {
                              badgeName: t(
                                  "settings.subscription.activeCoupon.badge"
                              ),
                              badgeColor: "success",
                          }
                        : {}),
                },
                {
                    id: "settings-team",
                    label: "menu.team",
                    link: "/settings/team",
                    parentId: "account",
                },
                {
                    id: "settings-workspace",
                    label: "menu.workspace",
                    link: "/settings/workspace",
                    parentId: "account",
                },
                {
                    id: "settings-referral",
                    label: "menu.referral",
                    link: "/settings/referral",
                    parentId: "account",
                }] : []),
                {
                    id: "settings-account",
                    label: "menu.myAccount",
                    link: "/settings/account",
                    parentId: "account",
                },
            ],
        },
    ];

    return <React.Fragment>{menuItems}</React.Fragment>;
};

export default Navdata;
